'use client'

import Image from 'next/image'

interface HeroProps {
  title: string
  subtitle?: string
  imageUrl?: string
  description?: string
  variant?: 'photo' | 'blue'
}

export default function HeroSection({ title, subtitle, imageUrl, description, variant = 'photo' }: HeroProps) {
  if (variant === 'blue') {
    return (
      <section className="relative w-full min-h-[340px] overflow-hidden pt-0">
        {/* Deep blue gradient background */}
        <div
          className="absolute inset-0 z-0"
          style={{
            background: 'linear-gradient(135deg, #0a0f2e 0%, #0d1b5e 30%, #1a2f8a 55%, #0d1b5e 75%, #080d20 100%)',
          }}
        />

        {/* Fingerprint / radial overlay */}
        <div
          className="absolute inset-0 z-0 opacity-20"
          style={{
            backgroundImage: `radial-gradient(ellipse 60% 80% at 50% 50%,
              transparent 20%,
              rgba(255,255,255,0.04) 25%,
              transparent 30%,
              rgba(255,255,255,0.03) 35%,
              transparent 40%,
              rgba(255,255,255,0.025) 45%,
              transparent 50%,
              rgba(255,255,255,0.02) 55%,
              transparent 60%,
              rgba(255,255,255,0.015) 65%,
              transparent 70%,
              rgba(255,255,255,0.01) 75%,
              transparent 80%
            )`,
          }}
        />

        {/* Large "Ch" or number letters on the left */}
        <div
          className="absolute left-0 top-0 bottom-0 z-0 flex items-center"
          style={{
            fontSize: '280px',
            fontWeight: 900,
            color: 'transparent',
            WebkitTextStroke: '1px rgba(255,255,255,0.06)',
            lineHeight: 1,
            userSelect: 'none',
            paddingLeft: '20px',
          }}
        >
          Ch
        </div>

        {/* Right decorative "R" letter */}
        <div
          className="absolute right-8 top-0 bottom-0 z-0 flex items-center"
          style={{
            fontSize: '180px',
            fontWeight: 900,
            color: 'transparent',
            WebkitTextStroke: '1px rgba(255,255,255,0.06)',
            lineHeight: 1,
            userSelect: 'none',
          }}
        >
          R
        </div>

        {/* Top shine line */}
        <div
          className="absolute top-0 left-0 right-0 h-px z-0"
          style={{
            background: 'linear-gradient(90deg, transparent, rgba(255,120,0,0.4), transparent)',
          }}
        />

        {/* Horse silhouette - top right */}
        <div
          className="absolute top-4 right-24 z-1 opacity-30"
          style={{ fontSize: '80px', lineHeight: 1, userSelect: 'none' }}
        >
          🐎
        </div>

        {/* Centered title */}
        <div className="relative z-10 flex items-center justify-center min-h-[340px] pt-20">
          <div className="text-center px-4">
            <h1
              className="text-4xl md:text-5xl font-black text-white tracking-widest uppercase"
              style={{ letterSpacing: '0.12em' }}
            >
              {title}
            </h1>
            {subtitle && (
              <p className="mt-3 text-lg font-semibold text-orange-400 uppercase tracking-wider">
                {subtitle}
              </p>
            )}
            {description && (
              <p className="mt-4 text-base text-white/70 max-w-2xl mx-auto">
                {description}
              </p>
            )}
          </div>
        </div>

        {/* Chat bubble icon */}
        <div
          className="absolute bottom-6 right-6 z-10 w-11 h-11 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(255,120,0,0.9)' }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
          </svg>
        </div>
      </section>
    )
  }

  // variant === 'photo' — dark collage style (Blog, Contact, Inspirations)
  return (
    <section className="relative w-full min-h-[340px] overflow-hidden pt-0">
      {/* Base dark background */}
      <div
        className="absolute inset-0 z-0"
        style={{ background: '#0a0c12' }}
      />

      {/* Left Polaroid photo */}
      <div
        className="absolute left-0 top-0 bottom-0 z-1 flex items-center"
        style={{ width: '40%' }}
      >
        {imageUrl ? (
          <div
            className="relative ml-8 mt-8"
            style={{
              transform: 'rotate(-4deg)',
              background: '#fff',
              padding: '8px 8px 30px 8px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
              width: '220px',
            }}
          >
            <img
              src={imageUrl}
              alt={title}
              className="w-full object-cover"
              style={{ height: '160px' }}
            />
          </div>
        ) : (
          <div
            className="ml-8 mt-8 relative"
            style={{
              transform: 'rotate(-4deg)',
              background: '#111',
              padding: '8px 8px 30px 8px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
              width: '220px',
              height: '200px',
            }}
          />
        )}
      </div>

      {/* Right photo card */}
      <div
        className="absolute right-0 top-0 bottom-0 z-1 flex items-start justify-end"
        style={{ width: '40%' }}
      >
        <div
          className="relative mr-8 mt-10"
          style={{
            transform: 'rotate(3deg)',
            background: '#0d2060',
            padding: '6px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
            width: '200px',
            height: '180px',
            overflow: 'hidden',
          }}
        >
          <div
            className="w-full h-full"
            style={{
              background: 'linear-gradient(135deg, #0d1b5e, #1a2f8a)',
              opacity: 0.8,
            }}
          />
        </div>
      </div>

      {/* Newspaper / scattered paper overlay */}
      <div
        className="absolute inset-0 z-1 opacity-10"
        style={{
          backgroundImage: `repeating-linear-gradient(
            0deg,
            transparent,
            transparent 18px,
            rgba(255,255,255,0.03) 18px,
            rgba(255,255,255,0.03) 19px
          )`,
        }}
      />

      {/* Dark overlay gradient over everything */}
      <div
        className="absolute inset-0 z-2"
        style={{
          background: 'linear-gradient(to right, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.15) 60%, rgba(0,0,0,0.55) 100%)',
        }}
      />

      {/* "Ch" letters - large left */}
      <div
        className="absolute left-0 bottom-0 z-2 opacity-40"
        style={{
          fontSize: '220px',
          fontWeight: 900,
          color: 'transparent',
          WebkitTextStroke: '1px rgba(255,255,255,0.15)',
          lineHeight: 1,
          userSelect: 'none',
        }}
      >
        Ch
      </div>

      {/* "R" letter - large right */}
      <div
        className="absolute right-4 top-8 z-2 opacity-40"
        style={{
          fontSize: '160px',
          fontWeight: 900,
          color: 'transparent',
          WebkitTextStroke: '1px rgba(255,255,255,0.12)',
          lineHeight: 1,
          userSelect: 'none',
        }}
      >
        R
      </div>

      {/* Centered title */}
      <div className="relative z-10 flex items-center justify-center min-h-[340px] pt-20">
        <div className="text-center px-4">
          <h1
            className="text-4xl md:text-5xl font-black text-white tracking-widest uppercase"
            style={{ letterSpacing: '0.12em' }}
          >
            {title}
          </h1>
          {subtitle && (
            <p className="mt-3 text-lg font-semibold text-orange-400 uppercase tracking-wider">
              {subtitle}
            </p>
          )}
          {description && (
            <p className="mt-4 text-base text-white/70 max-w-2xl mx-auto">
              {description}
            </p>
          )}
        </div>
      </div>

      {/* Chat bubble icon */}
      <div
        className="absolute bottom-6 right-6 z-10 w-11 h-11 rounded-full flex items-center justify-center"
        style={{ background: 'rgba(255,120,0,0.9)' }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
        </svg>
      </div>
    </section>
  )
}
